import type { AuthUser } from '~/types/auth'
import { ALLOWED_LOGIN_ROLES } from '~/constants/auth'

export const useAuthUser    = () => useState<AuthUser | null>('auth.user', () => null)
export const useAuthLoading = () => useState<boolean>('auth.loading', () => false)
export const useAuthReady   = () => useState<boolean>('auth.ready', () => false)

/** Dilempar saat login berhasil tapi role user tidak diizinkan mengakses academy panel. */
export class UnauthorizedRoleError extends Error {}

export function useAuth() {
  const user    = useAuthUser()
  const loading = useAuthLoading()

  const loggedIn = computed(() => !!user.value)

  // ── API calls (via Nitro — token never exposed to client) ─────────────────

  async function fetchUser(): Promise<AuthUser | null> {
    try {
      const data = await $fetch<AuthUser>('/api/auth/me')
      if (!data.roles_ids?.some(role => ALLOWED_LOGIN_ROLES.includes(role))) {
        await logout()
        throw new UnauthorizedRoleError('Role not allowed for academy panel')
      }
      user.value = data
      return data
    } catch (err) {
      user.value = null
      if (err instanceof UnauthorizedRoleError) throw err
      return null
    }
  }

  async function login(username: string, password: string) {
    await $fetch('/api/auth/login', {
      method: 'POST',
      body:   { username, password },
    })
    await fetchUser()
  }

  async function logout() {
    try {
      await $fetch('/api/auth/logout')
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        console.warn('Logout error:', err)
      }
    }
    user.value = null
    await navigateTo('/login')
  }

  async function init() {
    if (user.value) return
    try {
      await fetchUser()
    } catch (err) {
      // UnauthorizedRoleError sudah menangani logout + redirect di fetchUser();
      // di sini cukup jangan sampai app boot crash karenanya.
      if (!(err instanceof UnauthorizedRoleError)) throw err
    }
  }

  return {
    user,
    loggedIn,
    loading,
    login,
    logout,
    fetchUser,
    init,
  }
}
